import {
  HOLDER_FINISH,
  PLAYER_COLOR_PALETTE,
  isHolderColor,
  isPaletteColor,
} from "@solblox/shared";

/**
 * Deterministic fallback color for a player who hasn't picked one yet. Hashes
 * the identity (pubkey if available, otherwise sessionId) into the palette so
 * the same wallet keeps the same default across reconnects.
 */
export function derivedSkinColor(identity: string): string {
  let hash = 0;
  for (let i = 0; i < identity.length; i++) {
    hash = (hash * 31 + identity.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % PLAYER_COLOR_PALETTE.length;
  return PLAYER_COLOR_PALETTE[idx] ?? PLAYER_COLOR_PALETTE[0]!;
}

/**
 * Pick the rendered color: server-confirmed pick wins, else the deterministic
 * fallback. Identity must be stable for the lifetime of the avatar (pubkey or
 * sessionId — pubkey preferred when present).
 */
export function resolveSkinColor(colorHex: string, identity: string): string {
  const lower = colorHex.toLowerCase();
  // Accept any valid pick (base OR holder-exclusive); else hash a base default.
  if (isPaletteColor(lower)) return lower;
  return derivedSkinColor(identity || "guest");
}

/**
 * meshStandardMaterial props for an avatar capsule of the given color. Holder
 * colors get the metallic + emissive "special finish"; base colors render flat
 * (explicit defaults so a holder→base switch resets cleanly). Shared by the
 * local capsule (ControlsEcctrl) and remote/king avatars (PlayerAvatarBody) so
 * every body renders identically.
 */
export function avatarMaterialProps(hex: string): {
  color: string;
  metalness: number;
  roughness: number;
  emissive: string;
  emissiveIntensity: number;
} {
  const finish = isHolderColor(hex);
  return {
    color: hex,
    metalness: finish ? HOLDER_FINISH.metalness : 0,
    roughness: finish ? HOLDER_FINISH.roughness : 1,
    emissive: finish ? hex : "#000000",
    emissiveIntensity: finish ? HOLDER_FINISH.emissiveIntensity : 0,
  };
}
